import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concat, filter, map, mergeMap, of, switchMap } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { PersonService } from '../../services/person.service';
import { PersonActions } from '@states/person/person.actions';
import { SharedActions } from '@states/shared/shared.action-types';
import { UtilsService } from '../../edge/utils.service';
import { personFeature } from '@states/person/person.feature';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogModel } from '@ui';
import { PersonModel } from '@models/person.model';
import { UiUploadService } from '../../shared/ui-kit/ui-upload/ui-upload.service';
import { AlertModel } from '@models/alert.model';

export const getSavedPersons$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.getSavedPersons),
      switchMap(() =>
        personService.getSavedPersons().pipe(
          switchMap((persons) => [
            PersonActions.getSavedPersonsSuccess({ persons }),
          ]),
          catchError((response) => [
            PersonActions.getSavedPersonsFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const getUnsavedPersons$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.getUnsavedPersons),
      switchMap(() =>
        personService.getUnsavedPersons().pipe(
          switchMap((response) => [
            PersonActions.getUnsavedPersonsSuccess({ persons: response?.persons ?? [], token: response?.token }),
          ]),
          catchError((response) => [
            PersonActions.getUnsavedPersonsFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const bulkDeleteSavedPersons$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.bulkDeleteSavedPersons),
      switchMap(({ personIds }) =>
        personService.deletePersons(personIds).pipe(
          switchMap((deletedPersonIds) => [
            PersonActions.bulkDeleteSavedPersonsSuccess({
              personIds: deletedPersonIds?.length ? deletedPersonIds : personIds,
            }),
            PersonActions.getUnsavedPersons(),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.PERSONS_DELETED_SUCCESS') }),
          ]),
          catchError((response) =>
            getBulkDeleteSavedPersonsFailActions(response, personIds, utilsService, translateService),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const updateSavedPerson$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.updateSavedPerson),
      switchMap(({ person }) =>
        personService.updatePerson(person).pipe(
          switchMap(() => [
            PersonActions.updateSavedPersonSuccess({ person }),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.PERSON_UPDATED_SUCCESS') }),
          ]),
          catchError((response) => [
            PersonActions.updateSavedPersonFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const bulkCreatePendingPersons$ = createEffect(
  (
    actions$ = inject(Actions),
    store$ = inject(Store),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.bulkCreatePendingPersons),
      concatLatestFrom(() => store$.select(personFeature.selectUnsavedPersonsToken)),
      switchMap(([{ persons }, token]) => {
        if (!token) {
          return [
            PersonActions.bulkCreatePendingPersonsFail(),
            SharedActions.showMessage({
              error: translateService.instant('TOASTS.FAILED_TO_SAVE', {
                entity: translateService.instant('PEOPLE.UNSAVED').toLowerCase(),
              }),
            }),
          ];
        }

        const bucketIds = persons.map((person) => person.bucketId);

        return personService.bulkCreatePendingPersons({ data: persons, token }).pipe(
          switchMap((createdPersons) => [
            PersonActions.bulkCreatePendingPersonsSuccess({
              bucketIds,
              persons: createdPersons.map((person, index) => ({
                personId: person.personId,
                name: person.name ?? persons[index]?.name,
                bestImage: person.bestImage,
                bestTracker: person.bestTracker,
                trainingStatus: person.trainingStatus,
                isHidden: false,
              })),
            }),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.PERSONS_ADDED_SUCCESS') }),
          ]),
          catchError((response) => [
            PersonActions.bulkCreatePendingPersonsFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        );
      }),
    ),
  { functional: true },
);

export const getSuggestedTrackedPersons$ = createEffect(
  (
    actions$ = inject(Actions),
    store$ = inject(Store),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.getSuggestedTrackedPersons),
      concatLatestFrom(() => store$.select(personFeature.selectUnsavedPersonsToken)),
      switchMap(([{ bucketId }, token]) => {
        if (!token) {
          return [
            PersonActions.getSuggestedTrackedPersonsFail(),
            SharedActions.showMessage({ error: 'Failed to load suggested trackers' }),
          ];
        }

        return personService.getSuggestedTrackedPersons(token, bucketId).pipe(
          switchMap((persons) => [
            PersonActions.getSuggestedTrackedPersonsSuccess({ persons }),
          ]),
          catchError((response) => [
            PersonActions.getSuggestedTrackedPersonsFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        );
      }),
    ),
  { functional: true },
);

export const syncPersonImageUploadProgress$ = createEffect(
  (
    uploadService = inject(UiUploadService),
    store$ = inject(Store),
  ) =>
    uploadService.uploadProgress$.pipe(
      concatLatestFrom(() => store$.select(personFeature.selectIsUploadingPersonImage)),
      filter(([, isUploading]) => isUploading),
      map(([upload]) => {
        const progress = upload.progress <= 1 ? upload.progress * 100 : upload.progress;
        return PersonActions.setPersonImageUploadProgress({ progress: Math.round(progress) });
      }),
    ),
  { functional: true },
);

export const uploadPersonImage$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.uploadPersonImage),
      switchMap(({ file }) => {
        const asset: PersonModel.FaceAsset = {
          filename: `${Date.now()}_${file.name}`,
        };

        return personService.getFaceAssetPreSignedUrl({ asset }, file).pipe(
          switchMap((presignedResponse) =>
            personService.uploadFaceAssetPresignedUrl({
              url: presignedResponse.url,
              file: presignedResponse.file,
              filename: presignedResponse.filename,
            }).pipe(
              switchMap(() =>
                concat(
                  of(PersonActions.evaluatePersonImageStarted()),
                  personService.evaluateFaceImage({ fileUrls: [presignedResponse.filename] }).pipe(
                    switchMap((response) => getUploadPersonImageResultActions(response)),
                  ),
                ),
              ),
            ),
          ),
          catchError((response) => [
            PersonActions.uploadPersonImageFail({ errorKey: getUploadErrorKey(response) }),
          ]),
        );
      }),
    ),
  { functional: true },
);

export const createPerson$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.createPerson),
      switchMap(({ request }) =>
        personService.createPersonV2(request).pipe(
          switchMap((personId) => [
            PersonActions.createPersonSuccess({ personId }),
            PersonActions.getSavedPersons(),
            PersonActions.getUnsavedPersons(),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.PERSON_ADDED_SUCCESS') }),
          ]),
          catchError((response) => [
            PersonActions.createPersonFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const trainSavedPerson$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.trainSavedPerson),
      mergeMap(({ request }) =>
        personService.sendPendingSuggestedTrackers(request).pipe(
          switchMap((response) => [
            PersonActions.trainSavedPersonSuccess({ response }),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.PERSON_UPDATED_SUCCESS') }),
          ]),
          catchError((response) => [
            PersonActions.trainSavedPersonFail({ personId: request.personId }),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const attachPersonImage$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.attachPersonImage),
      switchMap(({ request }) =>
        personService.attachFaceImage(request).pipe(
          switchMap(() => [
            PersonActions.attachPersonImageSuccess({ personId: request.personId }),
            PersonActions.getSavedPersons(),
            PersonActions.getUnsavedPersons(),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.PERSON_ADDED_SUCCESS') }),
          ]),
          catchError((response) => [
            PersonActions.attachPersonImageFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

export const getPersonsDetails$ = createEffect(
  (
    actions$ = inject(Actions),
    personService = inject(PersonService),
    utilsService = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(PersonActions.getPersonsDetails),
      switchMap(({ personIds }) =>
        personService.getPersonsDetails(personIds).pipe(
          switchMap((persons) => [
            PersonActions.getPersonsDetailsSuccess({ persons }),
          ]),
          catchError((response) => [
            PersonActions.getPersonsDetailsFail(),
            SharedActions.showMessage({ error: utilsService.errMessage(response) }),
          ]),
        ),
      ),
    ),
  { functional: true },
);

function getBulkDeleteSavedPersonsFailActions(
  response: HttpErrorResponse | Error,
  personIds: number[],
  utilsService: UtilsService,
  translateService: TranslateService,
): Action[] {
  const events = getDeletePersonsProtectedEvents(response);

  if (events.length) {
    const eventsList = events
      .map(
        (event) =>
          `<li><a href="/alert/edit/${event._id}" target="_blank" style="color: var(--color-link); text-decoration: underline;">${event.name}</a></li>`,
      )
      .join('');
    const descriptionKey =
      personIds.length === 1
        ? 'DELETE_PEOPLE_MODAL.ASSOCIATED_EVENTS_SINGLE_DESCRIPTION'
        : 'DELETE_PEOPLE_MODAL.ASSOCIATED_EVENTS_MULTIPLE_DESCRIPTION';
    const titleKey = personIds.length === 1 ? 'DELETE_PEOPLE_MODAL.TITLE' : 'DELETE_PEOPLE_MODAL.MULTIPLE_TITLE';

    return [
      PersonActions.bulkDeleteSavedPersonsFail({ personIds }),
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.INFO,
          title: translateService.instant(titleKey),
          msg: `${translateService.instant(descriptionKey, { eventCounter: events.length })}<ul style="margin: 8px 0; padding-left: 20px;">${eventsList}</ul>`,
          disableClose: true,
        },
      }),
    ];
  }

  const conflictActions = AlertModel.buildEventsInUseConflictActions(response, 'GROUP_IN_USE_MODAL', translateService);
  if (conflictActions) {
    return [PersonActions.bulkDeleteSavedPersonsFail({ personIds }), ...conflictActions];
  }

  return [
    PersonActions.bulkDeleteSavedPersonsFail({ personIds }),
    SharedActions.showMessage({ error: utilsService.errMessage(response) }),
  ];
}

function getDeletePersonsProtectedEvents(response: HttpErrorResponse | Error): PersonModel.API.EventSummaryResponse[] {
  if (!(response instanceof HttpErrorResponse)) {
    return [];
  }

  const error = response.error as Partial<PersonModel.API.DeletePersonsProtectedError> | undefined;
  if (!Array.isArray(error?.events)) {
    return [];
  }

  return error.events.filter((event): event is PersonModel.API.EventSummaryResponse => Boolean(event?._id && event?.name));
}

function getUploadPersonImageResultActions(response: PersonModel.EvaluateFaceResponse): Action[] {
  if (response.code === PersonModel.FaceReturnMlCode.Success) {
    return [PersonActions.uploadPersonImageSuccess({ response })];
  }

  return [
    PersonActions.uploadPersonImageFail({
      errorKey: getUploadErrorKey(response.code),
    }),
  ];
}

function getUploadErrorKey(response: unknown): PersonModel.UploadErrorKey {
  const code = getFaceReturnMlCode(response);

  switch (code) {
    case PersonModel.FaceReturnMlCode.EmptyRequest:
      return PersonModel.UploadErrorKey.NoFaceDetected;
    case PersonModel.FaceReturnMlCode.InsufficientQuality:
      return PersonModel.UploadErrorKey.LowImageQuality;
    case PersonModel.FaceReturnMlCode.Unknown:
    default:
      return PersonModel.UploadErrorKey.Unknown;
  }
}

function getFaceReturnMlCode(response: unknown): PersonModel.FaceReturnMlCode {
  if (typeof response === 'number') {
    return response as PersonModel.FaceReturnMlCode;
  }

  if (response instanceof HttpErrorResponse) {
    const parsedCode = Number(response.error?.message?.trim?.());
    if (Number.isFinite(parsedCode)) {
      return parsedCode as PersonModel.FaceReturnMlCode;
    }
  }

  return PersonModel.FaceReturnMlCode.Unknown;
}
